import React, { useState } from 'react'
import { CBadge, CButton, CCollapse, CSmartTable } from '@coreui/react-pro'
import CommunityWinnerModal from './modals/CommunityWinnerModal'
import CommunityRoundWinnerModal from './modals/CommunityRoundWinnerModal'
import CommunityTopTeamsModal from './modals/CommunityTopTeamsModal'
import CommunityPlayerOfRoundModal from './modals/CommunityPlayerOfRoundModal'
import CommunityTopPlayersModal from './modals/CommunityTopPlayersModal'
import CommunityRankingUpdateModal from './modals/CommunityRankingUpdateModal'
import './CommunityPosts.css'

// The listing only filters on `title`/`source` and only sorts on real columns, so payload
// fields are display-only here — offering a filter row or sorter on them did nothing.
const PAYLOAD_COLUMN = { filter: false, sorter: false }

const BASE_COLUMNS = [
  { key: 'reactions', label: 'Reactions', filter: false, sorter: false, _style: { width: '18%' } },
  { key: 'created_at', label: 'Created', filter: false, sorter: true, _style: { width: '14%' } },
  { key: 'source', label: 'Source', filter: false, sorter: true, _style: { width: '10%' } },
  { key: 'show_details', label: 'Details', filter: false, sorter: false, _style: { width: '1%' } },
]

const COLUMNS_BY_TYPE = {
  winner: [
    { key: 'payload.full_name', label: 'User Name', ...PAYLOAD_COLUMN },
    { key: 'payload.team_name', label: 'Team Name', ...PAYLOAD_COLUMN },
    { key: 'payload.overall_rank', label: 'Rank', ...PAYLOAD_COLUMN },
    { key: 'payload.overall_points', label: 'Points', ...PAYLOAD_COLUMN },
  ],
  round_winner: [
    { key: 'payload.full_name', label: 'Winner Name', ...PAYLOAD_COLUMN },
    { key: 'payload.team_name', label: 'Team Name', ...PAYLOAD_COLUMN },
    { key: 'round_number', label: 'Round', filter: false, sorter: false },
  ],
  round_top_teams: [
    { key: 'title', label: 'Team Name', filter: false, sorter: true },
    { key: 'short_description', label: 'Round', filter: false, sorter: false },
  ],
  player_of_round: [
    { key: 'payload.player_name', label: 'Player Name', ...PAYLOAD_COLUMN },
    { key: 'payload.team_name', label: 'Team Name', ...PAYLOAD_COLUMN },
    { key: 'round_number', label: 'Round', filter: false, sorter: false },
    { key: 'payload.round_points', label: 'Points', ...PAYLOAD_COLUMN },
  ],
  round_top_players: [
    { key: 'title', label: 'Title', filter: false, sorter: true },
    { key: 'short_description', label: 'Description', filter: false, sorter: false },
  ],
  ranking_update: [
    { key: 'title', label: 'Title', filter: false, sorter: true },
    { key: 'short_description', label: 'Description', filter: false, sorter: false },
  ],
}

export const getColumns = (postType) => [
  ...(COLUMNS_BY_TYPE[postType] || [{ key: 'title', label: 'Title', filter: false, sorter: true }]),
  ...BASE_COLUMNS,
]

const REACTIONS = [
  { type: 'like', icon: '👍', label: 'Likes', key: 'like_count' },
  { type: 'fire', icon: '🔥', label: 'Fire', key: 'fire_count' },
  { type: 'love', icon: '❤️', label: 'Love', key: 'love_count' },
  { type: 'clap', icon: '👏', label: 'Claps', key: 'clap_count' },
  { type: 'wow', icon: '😲', label: 'Wows', key: 'wow_count' },
]

const SOURCE_COLORS = { system: 'success', user: 'info', admin: 'warning' }

const CommunityPostsTable = ({
  posts,
  postType,
  loading,
  setActivePage,
  setItemsPerPage,
  setColumnSorter,
  itemsPerPage,
  activePage,
}) => {
  const [details, setDetails] = useState([])

  const toggleDetails = (index) => {
    const position = details.indexOf(index)
    let newDetails = details.slice()
    if (position !== -1) {
      newDetails.splice(position, 1)
    } else {
      newDetails = [...details, index]
    }
    setDetails(newDetails)
  }

  const columns = getColumns(postType)

  const renderDetailsByType = (item) => {
    switch (postType) {
      case 'winner':
        return <CommunityWinnerModal item={item} />
      case 'round_winner':
        return <CommunityRoundWinnerModal item={item} />
      case 'round_top_teams':
        return <CommunityTopTeamsModal item={item} />
      case 'player_of_round':
        return <CommunityPlayerOfRoundModal item={item} />
      case 'round_top_players':
        return <CommunityTopPlayersModal item={item} />
      case 'ranking_update':
        return <CommunityRankingUpdateModal item={item} />
      default:
        return <pre className="cp-payload">{JSON.stringify(item.payload, null, 2)}</pre>
    }
  }

  const scopedColumns = {
    reactions: (item) => {
      const used = REACTIONS.filter((reaction) => Number(item[reaction.key]) > 0)
      return (
        <td>
          {used.length === 0 ? (
            <span className="cp-muted">No reactions</span>
          ) : (
            <div className="cp-reactions">
              {used.map((reaction) => (
                <span className="cp-reaction" key={reaction.type} title={reaction.label}>
                  {reaction.icon}
                  <strong>{item[reaction.key]}</strong>
                </span>
              ))}
            </div>
          )}
        </td>
      )
    },
    created_at: (item) => (
      <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}</td>
    ),
    source: (item) => (
      <td>
        <CBadge
          color={SOURCE_COLORS[item.source] || 'primary'}
          shape="rounded-pill"
          className="px-2 py-1"
        >
          {item.source || '-'}
        </CBadge>
      </td>
    ),
    title: (item) => <td className="fw-semibold">{item.title || '-'}</td>,
    short_description: (item) => <td>{item.short_description || '-'}</td>,
    round_number: (item) => <td>{item.round_number ?? '-'}</td>,
    show_details: (item) => (
      <td>
        <CButton color="primary" variant="outline" size="sm" onClick={() => toggleDetails(item.id)}>
          {details.includes(item.id) ? 'Hide' : 'View'}
        </CButton>
      </td>
    ),
    details: (item) => (
      <CCollapse visible={details.includes(item.id)}>
        <div className="cp-details">
          {renderDetailsByType(item)}
          <div className="cp-detail mt-3">
            <span className="cp-detail-label">Reactions</span>
            <div className="cp-reactions mt-1">
              {REACTIONS.map((reaction) => (
                <span className="cp-reaction" key={reaction.type}>
                  {reaction.icon} {reaction.label}
                  <strong>{item[reaction.key] || 0}</strong>
                </span>
              ))}
            </div>
          </div>
        </div>
      </CCollapse>
    ),
  }

  // Payload fields are nested, so give each one a renderer that reaches into payload.
  columns.forEach((col) => {
    if (col.key.startsWith('payload.')) {
      const payloadKey = col.key.split('.')[1]
      scopedColumns[col.key] = (item) => {
        const value = item.payload?.[payloadKey]
        return <td>{value !== null && value !== undefined && value !== '' ? value : '-'}</td>
      }
    }
  })

  return (
    <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
      <CSmartTable
        columns={columns}
        columnSorter={{ external: true }}
        noItemsLabel="No posts of this type yet"
        scopedColumns={scopedColumns}
        items={posts?.data}
        itemsPerPage={itemsPerPage}
        itemsPerPageSelect
        loading={loading}
        pagination={{ external: true }}
        paginationProps={{
          activePage,
          pages: Math.ceil(posts?.total / itemsPerPage) || 1,
        }}
        tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
        onActivePageChange={(page) => setActivePage(page)}
        onItemsPerPageChange={(perPage) => {
          setActivePage(1)
          setItemsPerPage(perPage)
        }}
        onSorterChange={(sorter) => setColumnSorter(sorter)}
      />
    </div>
  )
}

export default CommunityPostsTable
